import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Phone, Plus, Edit, Trash2, Users, History, Server,
  PhoneCall, PhoneIncoming, PhoneMissed, Save,
  CheckCircle, XCircle, Loader2, Home, User, AlertCircle, BookOpen, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '@/utils/userHelper';

// Department options for directory
const DEPARTMENTS = [
  'Reception',
  'Front Desk', 
  'Room Service',
  'Housekeeping',
  'Concierge',
  'Restaurant',
  'Spa',
  'Security',
  'Maintenance',
  'Management',
  'Other'
];

export default function PhoneSettings({ user: propUser }) {
  const user = useUser(propUser);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('directory');
  
  // Extensions (created by Super Admin)
  const [extensions, setExtensions] = useState([]);
  
  // Directory entries - extensions that have been configured with directory info
  const [directory, setDirectory] = useState([]);
  
  // Rooms for assignment
  const [rooms, setRooms] = useState([]);
  
  // Users for assignment
  const [hotelUsers, setHotelUsers] = useState([]);
  
  // Dialog state
  const [directoryDialog, setDirectoryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [directoryForm, setDirectoryForm] = useState({
    name: '',
    department: '',
    description: '',
    assigned_extension_id: '',
    assignment_type: 'none',
    assigned_room_id: '',
    assigned_room_number: '',
    assigned_user_id: '',
    assigned_user_name: '',
    is_public: true
  });
  const [directorySaving, setDirectorySaving] = useState(false);
  
  // Call logs
  const [callLogs, setCallLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // SIP Config status
  const [sipConfigured, setSipConfigured] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Check if SIP is configured
      const configRes = await api.get('/sip/config').catch(() => ({ data: null }));
      setSipConfigured(!!configRes.data?.websocket_url);
      
      // Fetch extensions (created by Super Admin)
      const extRes = await api.get('/sip/directory').catch(() => ({ data: [] }));
      const allExtensions = extRes.data || [];
      setExtensions(allExtensions);
      
      // Directory = extensions that have directory info (department set or has assignment)
      // An extension is in directory if it has been "configured" by hotel admin
      const directoryEntries = allExtensions.filter(e => 
        e.is_in_directory === true || 
        (e.department && e.department.trim() !== '') ||
        (e.assignment_type && e.assignment_type !== 'none')
      );
      setDirectory(directoryEntries);
      
      // Fetch rooms for this hotel
      try {
        const roomsRes = await api.get('/rooms');
        setRooms(roomsRes.data || []);
      } catch (e) {
        console.log('Could not fetch rooms');
        setRooms([]);
      }
      
      // Fetch hotel users for assignment dropdown
      try {
        const usersRes = await api.get('/users');
        setHotelUsers(usersRes.data || []);
      } catch (e) {
        console.log('Could not fetch users');
        setHotelUsers([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch phone settings:', error);
      toast.error('Failed to load phone settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCallLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await api.get('/sip/call-logs?limit=100');
      setCallLogs(res.data?.logs || []);
    } catch (error) {
      toast.error('Failed to load call logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Get extensions not yet in directory (available for assignment)
  const getAvailableExtensions = () => {
    // An extension is "available" if:
    // 1. It's NOT marked as in_directory
    // 2. AND it doesn't have an assignment (room or user)
    // Unless we're editing, then include the current extension
    return extensions.filter(e => {
      // If editing, always include the current extension
      if (editingEntry && e.id === editingEntry.id) {
        return true;
      }
      // Filter out extensions that are already in directory or have assignments
      const isInDirectory = e.is_in_directory === true;
      const hasAssignment = e.assignment_type && e.assignment_type !== 'none';
      return !isInDirectory && !hasAssignment;
    });
  };

  // Get rooms not yet assigned to any directory entry
  const getAvailableRooms = () => {
    const assignedRoomIds = directory
      .filter(d => d.assignment_type === 'room' && d.assigned_room_id)
      .map(d => d.assigned_room_id);
    
    // If editing a room assignment, include the currently assigned room
    return rooms.filter(r => 
      !assignedRoomIds.includes(r.id) || 
      (editingEntry && r.id === editingEntry.assigned_room_id)
    );
  };

  const openAddDirectory = () => {
    setEditingEntry(null);
    setDirectoryForm({
      name: '',
      department: '',
      description: '',
      assigned_extension_id: '',
      assignment_type: 'none',
      assigned_room_id: '',
      assigned_room_number: '',
      assigned_user_id: '',
      assigned_user_name: '',
      is_public: true
    });
    setDirectoryDialog(true);
  };

  const openEditDirectory = (entry) => {
    setEditingEntry(entry);
    setDirectoryForm({
      name: entry.name || '',
      department: entry.department || '',
      description: entry.description || '',
      assigned_extension_id: entry.id || '',
      assignment_type: entry.assignment_type || 'none',
      assigned_room_id: entry.assigned_room_id || '',
      assigned_room_number: entry.assigned_room_number || '',
      assigned_user_id: entry.assigned_user_id || '',
      assigned_user_name: entry.assigned_user_name || '',
      is_public: entry.is_public ?? true
    });
    setDirectoryDialog(true);
  };

  const saveDirectory = async () => {
    if (!directoryForm.name) {
      toast.error('Name is required');
      return;
    }
    
    if (!directoryForm.assigned_extension_id) {
      toast.error('Please select an extension');
      return;
    }

    // Validate room selection if assignment type is room
    if (directoryForm.assignment_type === 'room' && !directoryForm.assigned_room_id) {
      toast.error('Please select a room');
      return;
    }
    
    setDirectorySaving(true);
    try {
      // Find the selected room to get room number
      const selectedRoom = rooms.find(r => r.id === directoryForm.assigned_room_id);
      // Find the selected user to get user name
      const selectedUser = hotelUsers.find(u => u.id === directoryForm.assigned_user_id);
      
      // Update the extension with directory info
      await api.put(`/sip/directory/${directoryForm.assigned_extension_id}`, {
        name: directoryForm.name,
        department: directoryForm.department,
        description: directoryForm.description,
        assignment_type: directoryForm.assignment_type,
        assigned_room_id: directoryForm.assignment_type === 'room' ? directoryForm.assigned_room_id : null,
        assigned_room_number: directoryForm.assignment_type === 'room' ? (selectedRoom?.room_number || directoryForm.assigned_room_number) : null,
        assigned_user_id: directoryForm.assignment_type === 'user' ? directoryForm.assigned_user_id : null,
        assigned_user_name: directoryForm.assignment_type === 'user' ? (selectedUser?.name || selectedUser?.email || directoryForm.assigned_user_name) : null,
        is_public: directoryForm.is_public,
        is_in_directory: true  // Mark as in directory
      });
      
      toast.success(editingEntry ? 'Directory entry updated' : 'Directory entry created');
      setDirectoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save directory entry');
    } finally {
      setDirectorySaving(false);
    }
  };

  const deleteDirectory = async (entry) => {
    if (!window.confirm('Remove this directory entry? The extension will remain available for reassignment.')) return;
    
    try {
      // Clear the directory info from the extension
      await api.put(`/sip/directory/${entry.id}`, {
        department: '',
        description: '',
        assignment_type: 'none',
        assigned_room_id: null,
        assigned_room_number: null,
        assigned_user_name: null,
        is_in_directory: false  // Mark as not in directory
      });
      toast.success('Directory entry removed');
      fetchData();  // Refresh data to update both lists
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to remove directory entry');
    }
  };

  // Format duration for call logs
  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const availableExtensions = getAvailableExtensions();
  const availableRooms = getAvailableRooms();

  return (
    <div className="space-y-6" data-testid="phone-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Server className="h-7 w-7 text-primary" />
            PBX / Phone System
          </h1>
          <p className="text-gray-500 mt-1">Create directory entries and assign extensions to rooms</p>
        </div>
        <Badge variant={sipConfigured ? "default" : "secondary"} className={sipConfigured ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>
          {sipConfigured ? (
            <><CheckCircle className="h-3 w-3 mr-1" /> SIP Configured</>
          ) : (
            <><AlertCircle className="h-3 w-3 mr-1" /> SIP Not Configured</>
          )}
        </Badge>
      </div>

      {!sipConfigured && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">SIP Server Not Configured</p>
                <p className="text-sm text-yellow-600">
                  Please contact Super Admin to configure the SIP server and create extensions for your hotel.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {extensions.length === 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">No Extensions Available</p>
                <p className="text-sm text-blue-600">
                  Super Admin needs to create extensions in PBX Management before you can create directory entries.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="directory" className="flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Directory ({directory.length})
          </TabsTrigger>
          <TabsTrigger value="extensions" className="flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Unassigned ({availableExtensions.length})
          </TabsTrigger>
          <TabsTrigger value="logs" className="flex items-center gap-2" onClick={fetchCallLogs}>
            <History className="h-4 w-4" />
            Call Logs
          </TabsTrigger>
        </TabsList>

        {/* Directory Tab */}
        <TabsContent value="directory" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Phone Directory
                </CardTitle>
                <CardDescription>
                  Create directory entries and assign extensions to rooms
                </CardDescription>
              </div>
              <Button 
                onClick={openAddDirectory} 
                disabled={availableExtensions.length === 0}
                data-testid="add-directory"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Directory Entry
              </Button>
            </CardHeader>
            <CardContent>
              {directory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No directory entries</p>
                  <p className="text-sm">Create directory entries to assign extensions to rooms</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Extension</TableHead>
                      <TableHead>Room Assignment</TableHead>
                      <TableHead>Public</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {directory.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">{entry.name}</TableCell>
                        <TableCell>{entry.department || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{entry.extension}</Badge>
                        </TableCell>
                        <TableCell>
                          {entry.assignment_type === 'room' && entry.assigned_room_number ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Home className="h-4 w-4 text-blue-500" />
                              <span>Room {entry.assigned_room_number}</span>
                            </div>
                          ) : entry.assignment_type === 'user' && entry.assigned_user_name ? (
                            <div className="flex items-center gap-1 text-sm">
                              <User className="h-4 w-4 text-purple-500" />
                              <span>{entry.assigned_user_name}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.is_public ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDirectory(entry)}
                              data-testid={`edit-dir-${entry.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDirectory(entry)}
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-dir-${entry.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Extensions Tab - Show ONLY unassigned extensions */}
        <TabsContent value="extensions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                Unassigned Extensions
              </CardTitle>
              <CardDescription>
                Extensions created by Super Admin that are not yet in the directory. Use "Add Directory Entry" to assign them.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {availableExtensions.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Phone className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">
                    {extensions.length === 0 ? 'No extensions available' : 'All extensions are assigned'}
                  </p>
                  <p className="text-sm">
                    {extensions.length === 0 
                      ? 'Contact Super Admin to create extensions' 
                      : 'All available extensions have been added to the directory'}
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Extension</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {availableExtensions.map((ext) => (
                      <TableRow key={ext.id}>
                        <TableCell className="font-medium">{ext.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{ext.extension}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={ext.is_active ? "default" : "secondary"}>
                            {ext.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingEntry(null);
                              setDirectoryForm({
                                name: ext.name || '',
                                department: '',
                                description: '',
                                assigned_extension_id: ext.id,
                                assignment_type: 'none',
                                assigned_room_id: '',
                                assigned_room_number: '',
                                assigned_user_name: '',
                                is_public: true
                              });
                              setDirectoryDialog(true);
                            }}
                            data-testid={`assign-ext-${ext.id}`}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add to Directory
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Call Logs Tab */}
        <TabsContent value="logs" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Call History
              </CardTitle>
              <CardDescription>Recent calls made through the phone system</CardDescription>
            </CardHeader>
            <CardContent>
              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : callLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <History className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">No call history</p>
                  <p className="text-sm">Calls will appear here once made</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callLogs.map((log) => {
                      const isOutbound = log.direction === 'outbound';
                      const isMissed = log.status === 'missed';
                      const Icon = isMissed ? PhoneMissed : (isOutbound ? PhoneCall : PhoneIncoming);
                      const iconColor = isMissed ? 'text-red-500' : 
                                       (isOutbound ? 'text-blue-500' : 'text-green-500');
                      
                      return (
                        <TableRow key={log.id}>
                          <TableCell>
                            <Icon className={`h-5 w-5 ${iconColor}`} />
                          </TableCell>
                          <TableCell>{log.caller_name || log.caller_id}</TableCell>
                          <TableCell>{log.callee_name || log.callee_id}</TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(log.started_at).toLocaleString()}
                          </TableCell>
                          <TableCell>{formatDuration(log.duration_seconds)}</TableCell>
                          <TableCell>
                            <Badge variant={
                              log.status === 'completed' ? 'default' :
                              log.status === 'missed' ? 'destructive' : 'secondary'
                            }>
                              {log.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Directory Entry Dialog */}
      <Dialog open={directoryDialog} onOpenChange={setDirectoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              {editingEntry ? 'Edit Directory Entry' : 'Add Directory Entry'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Directory Name *</Label>
              <Input
                placeholder="Front Desk"
                value={directoryForm.name}
                onChange={(e) => setDirectoryForm({ ...directoryForm, name: e.target.value })}
                data-testid="dir-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Select Extension *</Label>
              <Select
                value={directoryForm.assigned_extension_id}
                onValueChange={(v) => setDirectoryForm({ ...directoryForm, assigned_extension_id: v })}
              >
                <SelectTrigger data-testid="dir-extension">
                  <SelectValue placeholder="Select extension" />
                </SelectTrigger>
                <SelectContent>
                  {availableExtensions.map((ext) => (
                    <SelectItem key={ext.id} value={ext.id}>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {ext.name} - Ext: {ext.extension}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {availableExtensions.length === 0 && !editingEntry && (
                <p className="text-xs text-yellow-600">All extensions are already assigned</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Department</Label>
              <Select
                value={directoryForm.department}
                onValueChange={(v) => setDirectoryForm({ ...directoryForm, department: v })}
              >
                <SelectTrigger data-testid="dir-department">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Main reception desk"
                value={directoryForm.description}
                onChange={(e) => setDirectoryForm({ ...directoryForm, description: e.target.value })}
              />
            </div>

            {/* Room Assignment Section */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Home className="h-4 w-4" />
                Room Assignment
              </h4>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Assign To</Label>
                  <Select
                    value={directoryForm.assignment_type}
                    onValueChange={(v) => setDirectoryForm({ 
                      ...directoryForm, 
                      assignment_type: v,
                      assigned_room_id: '',
                      assigned_room_number: '',
                      assigned_user_id: '',
                      assigned_user_name: ''
                    })}
                  >
                    <SelectTrigger data-testid="dir-assignment-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Assigned</SelectItem>
                      <SelectItem value="room">Assign to Room</SelectItem>
                      <SelectItem value="user">Assign to User</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {directoryForm.assignment_type === 'room' && (
                  <div className="space-y-2">
                    <Label>Select Room *</Label>
                    <Select
                      value={directoryForm.assigned_room_id}
                      onValueChange={(v) => {
                        const room = rooms.find(r => r.id === v);
                        setDirectoryForm({ 
                          ...directoryForm, 
                          assigned_room_id: v,
                          assigned_room_number: room?.room_number || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="dir-room">
                        <SelectValue placeholder="Select room" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRooms.map((room) => (
                          <SelectItem key={room.id} value={room.id}>
                            <div className="flex items-center gap-2">
                              <Home className="h-4 w-4" />
                              Room {room.room_number} {room.room_type_name ? `- ${room.room_type_name}` : ''}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {availableRooms.length === 0 && (
                      <p className="text-xs text-yellow-600">All rooms are already assigned</p>
                    )}
                    <p className="text-xs text-gray-500">
                      Guest in this room can use this extension
                    </p>
                  </div>
                )}

                {directoryForm.assignment_type === 'user' && (
                  <div className="space-y-2">
                    <Label>Select User *</Label>
                    <Select
                      value={directoryForm.assigned_user_id}
                      onValueChange={(v) => {
                        const selectedUser = hotelUsers.find(u => u.id === v);
                        setDirectoryForm({ 
                          ...directoryForm, 
                          assigned_user_id: v,
                          assigned_user_name: selectedUser?.name || selectedUser?.email || ''
                        });
                      }}
                    >
                      <SelectTrigger data-testid="dir-user">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {hotelUsers.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4" />
                              {u.name || u.email} 
                              {u.role && <span className="text-xs text-gray-400">({u.role})</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hotelUsers.length === 0 && (
                      <p className="text-xs text-yellow-600">No users found in this hotel</p>
                    )}
                    <p className="text-xs text-gray-500">
                      This user can use this extension for making calls
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Public toggle */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div>
                <Label>Show in Guest Directory</Label>
                <p className="text-xs text-gray-500">Visible to guests in portal</p>
              </div>
              <input
                type="checkbox"
                checked={directoryForm.is_public}
                onChange={(e) => setDirectoryForm({ ...directoryForm, is_public: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300"
                data-testid="dir-public"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDirectoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveDirectory} disabled={directorySaving} data-testid="save-directory">
              {directorySaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingEntry ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
